import {
  AgeTrack,
  ApplicantProfile,
  ApplicationDraft,
  ApplicationRoute,
  ApplicationStepDef,
  ApplicationStepId,
  CohortRecord,
  EntityProfile,
  GuardianProfile,
  IconName,
  LearnerProfile,
  OnboardingDraft,
  OnboardingStepDef,
  OnboardingStepId,
  ProfessionalProfile,
  ProgramRecord,
  RoleCategory,
  RoleRecord,
  RootState,
} from "../types/domain";
import { makeReference } from "../utils/auth";
import { COUNTRY_OPTIONS } from "../../../shared/countries";
import { LANGUAGE_NAMES } from "../../../shared/languages";
export const APPLICATION_VERSION = "2.0.0";
export const STORAGE_KEY = "chatoner-academy-auth-application-onboarding-v2";
export const DEMO_EMAIL_CODE = "482731";
export const DEMO_MFA_CODE = "246810";
export const DEMO_INVITE_CODE = "ACADEMY-INVITE-2026";

export const ROLE_GROUPS: Array<{ id: RoleCategory; label: string; description: string }> = [
  {
    id: "learner",
    label: "Learners",
    description: "Individual, sponsored and youth learners applying to Academy Programs.",
  },
  {
    id: "family",
    label: "Parents & guardians",
    description: "Verified adults supporting age-appropriate learning, consent and billing.",
  },
  {
    id: "teaching",
    label: "Teaching & mentoring",
    description: "Instructors, mentors, assessors and instructor leadership.",
  },
  {
    id: "assessment",
    label: "Assessment operations",
    description: "Exam proctors, integrity reviewers, moderators and external examiners.",
  },
  {
    id: "organization",
    label: "Organizations",
    description: "Commercial, governance, people, finance, procurement and technical roles.",
  },
  {
    id: "institution",
    label: "Education institutions",
    description: "Academic leadership, registry, quality, support, records and IT roles.",
  },
  {
    id: "academy",
    label: "Academy operations",
    description: "Admissions, administration, safeguarding, accessibility and support.",
  },
];

export const ROLES: RoleRecord[] = [
  {
    id: "learner",
    label: "Learner / candidate",
    category: "learner",
    access: "public",
    description: "Apply to a Program for your own learning and credential journey.",
    workspace: "Learner Campus",
    mfa: "optional",
    route: "learner-self",
  },
  {
    id: "team-learner",
    label: "Sponsored team learner",
    category: "learner",
    access: "invitation",
    description: "Join a Program sponsored by an employer, organization or institution.",
    workspace: "Learner Campus + sponsor workspace",
    mfa: "recommended",
    route: "invited-member",
  },
  {
    id: "parent",
    label: "Parent",
    category: "family",
    access: "public",
    description: "Apply for a child, manage required consent and receive family-safe updates.",
    workspace: "Parent & Guardian Studio",
    mfa: "recommended",
    route: "parent-child",
  },
  {
    id: "guardian",
    label: "Legal guardian / authorized adult",
    category: "family",
    access: "public",
    description: "Support a learner through a verified, scoped relationship.",
    workspace: "Parent & Guardian Studio",
    mfa: "recommended",
    route: "parent-child",
  },
  {
    id: "instructor",
    label: "Instructor",
    category: "teaching",
    access: "application",
    description: "Teach assigned Programs, facilitate Build Rooms and conduct human review.",
    workspace: "Instructor Studio",
    mfa: "required",
    route: "teaching",
  },
  {
    id: "mentor",
    label: "Mentor",
    category: "teaching",
    access: "application",
    description: "Guide learners and teams without silently inheriting grading authority.",
    workspace: "Instructor Studio",
    mfa: "required",
    route: "teaching",
  },
  {
    id: "assessor",
    label: "Instructor & assessor",
    category: "teaching",
    access: "application",
    description: "Create, guide and grade within explicitly assigned scope.",
    workspace: "Instructor Studio + Labs assessor",
    mfa: "required",
    route: "teaching",
  },
  {
    id: "instructor-lead",
    label: "Instructor lead",
    category: "teaching",
    access: "invitation",
    description: "Coordinate instructor assignments, workload and quality controls.",
    workspace: "Instructor Studio",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "exam-proctor",
    label: "Exam proctor",
    category: "assessment",
    access: "application",
    description: "Operate assigned assessments, support candidates, record facts and escalate.",
    workspace: "Exam Proctor Studio",
    mfa: "required",
    route: "proctor",
  },
  {
    id: "integrity-reviewer",
    label: "Integrity reviewer",
    category: "assessment",
    access: "invitation",
    description: "Review factual incidents and candidate context under due process.",
    workspace: "Assessment Review",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "moderator",
    label: "Moderator / external examiner",
    category: "assessment",
    access: "invitation",
    description: "Review defined samples and decisions within time-limited appointed scope.",
    workspace: "Assessment Review",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "organization-owner",
    label: "Organization owner",
    category: "organization",
    access: "application",
    description: "Own the tenant relationship, privileged roles and commercial approvals.",
    workspace: "Organization Hub",
    mfa: "required",
    route: "organization",
  },
  {
    id: "program-manager",
    label: "Program manager",
    category: "organization",
    access: "invitation",
    description: "Coordinate programs, cohorts, schedules, success plans and reporting.",
    workspace: "Organization Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "people-admin",
    label: "People administrator",
    category: "organization",
    access: "invitation",
    description: "Manage members, teams, seats, enrolment and provisioning.",
    workspace: "Organization Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "team-manager",
    label: "Team manager",
    category: "organization",
    access: "invitation",
    description: "Support team enrolment, aggregate progress and adoption actions.",
    workspace: "Organization Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "governance-lead",
    label: "Governance lead",
    category: "organization",
    access: "invitation",
    description: "Manage policy, risk, tool approval, entitlements and audit scope.",
    workspace: "Organization Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "finance-admin",
    label: "Finance administrator",
    category: "organization",
    access: "invitation",
    description: "Manage plans, seats, invoices, usage and renewal assumptions.",
    workspace: "Organization Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "procurement-officer",
    label: "Procurement officer",
    category: "organization",
    access: "invitation",
    description: "Manage contracts, DPA, SOW, purchase orders and vendor evidence.",
    workspace: "Organization Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "technical-admin",
    label: "Technical administrator",
    category: "organization",
    access: "invitation",
    description: "Manage SSO, SCIM, integrations, webhooks and service health.",
    workspace: "Organization Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "organization-auditor",
    label: "Organization auditor",
    category: "organization",
    access: "invitation",
    description: "Read approved evidence and audit exports without editing authority.",
    workspace: "Organization Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "institution-owner",
    label: "Institution owner / principal administrator",
    category: "institution",
    access: "application",
    description: "Configure the institution, delegated administrators, contracts and high-level policy.",
    workspace: "Education Institution Hub",
    mfa: "required",
    route: "institution",
  },
  {
    id: "registrar",
    label: "Registrar / academic records",
    category: "institution",
    access: "invitation",
    description: "Manage official enrolment, results, progression, awards and transcripts.",
    workspace: "Education Institution Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "dean",
    label: "Dean / faculty leader",
    category: "institution",
    access: "invitation",
    description: "Lead assigned schools, programs, staffing, capacity and academic quality.",
    workspace: "Education Institution Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "department-head",
    label: "Head of department / program leader",
    category: "institution",
    access: "invitation",
    description: "Coordinate assigned courses, instructors, cohorts, timetables and quality.",
    workspace: "Education Institution Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "quality-team",
    label: "Quality & accreditation",
    category: "institution",
    access: "invitation",
    description: "Manage standards, evidence, findings and improvement actions.",
    workspace: "Education Institution Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "assessment-board",
    label: "Assessment board / moderator",
    category: "institution",
    access: "invitation",
    description: "Make documented academic decisions within appointed scope.",
    workspace: "Education Institution Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "student-success",
    label: "Student success / wellbeing / accessibility",
    category: "institution",
    access: "invitation",
    description: "Access minimum-necessary support information for assigned cases.",
    workspace: "Education Institution Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "research-ethics",
    label: "Research & ethics office",
    category: "institution",
    access: "invitation",
    description: "Manage research proposals, ethics, consent, data plans and approved outputs.",
    workspace: "Education Institution Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "institution-finance",
    label: "Institution finance / procurement",
    category: "institution",
    access: "invitation",
    description: "Manage budgets, invoices, purchase orders, sponsorship and cost centres.",
    workspace: "Education Institution Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "library-resources",
    label: "Library / learning resources",
    category: "institution",
    access: "invitation",
    description: "Manage approved resources, licences, access and repository metadata.",
    workspace: "Education Institution Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "institution-it",
    label: "Information security / IT / integration",
    category: "institution",
    access: "invitation",
    description: "Manage identity, SSO, SCIM, connections, health and incident response.",
    workspace: "Education Institution Hub",
    mfa: "required",
    route: "invited-member",
  },
  {
    id: "admissions-reviewer",
    label: "Admissions reviewer",
    category: "academy",
    access: "invitation",
    description: "Review applications, request information and record human decisions.",
    workspace: "Academy Operations",
    mfa: "required",
    route: "academy-staff",
  },
  {
    id: "academy-admin",
    label: "Academy administrator",
    category: "academy",
    access: "invitation",
    description: "Configure policy, access and operations without silently taking academic authority.",
    workspace: "Academy Operations",
    mfa: "required",
    route: "academy-staff",
  },
  {
    id: "safeguarding-officer",
    label: "Safeguarding officer",
    category: "academy",
    access: "invitation",
    description: "Handle protected safeguarding matters under minimum-necessary access.",
    workspace: "Academy Operations",
    mfa: "required",
    route: "academy-staff",
  },
  {
    id: "accessibility-officer",
    label: "Accessibility officer",
    category: "academy",
    access: "invitation",
    description: "Coordinate accommodations and accessible participation without inferring diagnoses.",
    workspace: "Academy Operations",
    mfa: "required",
    route: "academy-staff",
  },
  {
    id: "academy-support",
    label: "Academy support",
    category: "academy",
    access: "invitation",
    description: "Resolve account, technical, learning and billing requests within assigned scope.",
    workspace: "Academy Operations",
    mfa: "required",
    route: "academy-staff",
  },
];

export const EVERYDAY_TRACKS: AgeTrack[] = [
  {
    id: "age-5-7",
    label: "AI Early Explorers",
    ages: "5–7 years",
    min: 5,
    max: 7,
    guardianMode: "parent-primary",
    description: "Playful, guided AI literacy with a parent or guardian as the primary account holder.",
    learnerAccess: "Family-managed learner profile; no independent commercial or consent authority.",
  },
  {
    id: "age-8-11",
    label: "AI Young Creators",
    ages: "8–11 years",
    min: 8,
    max: 11,
    guardianMode: "parent-primary",
    description: "Age-appropriate creativity, verification and safety with active family participation.",
    learnerAccess: "Guardian-managed enrolment with age-appropriate learner assent and protected access.",
  },
  {
    id: "age-12-17",
    label: "AI Teens & Future Skills",
    ages: "12–17 years",
    min: 12,
    max: 17,
    guardianMode: "conditional",
    description:
      "Responsible study, creativity and future-ready AI use for secondary-school and youth learners.",
    learnerAccess:
      "Learner participation with guardian consent where required by age, country, sponsor or institution policy.",
  },
  {
    id: "age-18-plus",
    label: "AI Essentials for Adults",
    ages: "18+ years",
    min: 18,
    guardianMode: "adult",
    description: "Practical everyday AI for work, study, personal administration, freelancing and decisions.",
    learnerAccess: "Independent learner account; family access is optional, invited and revocable.",
  },
];

export const PROGRAMS: ProgramRecord[] = [
  {
    id: "everyday-ai",
    name: "AI Essentials for Everyday Life",
    level: "Foundation",
    duration: "3 weeks",
    commitment: "2–4 hours per week",
    category: "AI literacy",
    outcome:
      "A personal AI operating guide with responsible workflows, verification habits and measurable practice evidence.",
    project: "Design three responsible AI workflows for real work, learning or everyday tasks.",
    credential: "AI Operator — Foundation",
    audiences: ["learner-self", "parent-child", "organization", "institution"],
    minAge: 5,
    tracks: EVERYDAY_TRACKS,
    skills: ["AI literacy", "Prompting", "Verification", "Privacy", "Productivity"],
    icon: "sparkles",
  },
  {
    id: "prompt-context",
    name: "AI Prompt & Context Engineering",
    level: "Foundation",
    duration: "4 weeks",
    commitment: "4–6 hours per week",
    category: "AI orchestration",
    outcome:
      "A tested prompt-and-context operating pack with source grounding, evaluation criteria and human review.",
    project:
      "Build a reusable AI work system for a real role, including context, tests and approval criteria.",
    credential: "Prompt & Context Engineer",
    audiences: ["learner-self", "organization", "institution"],
    minAge: 16,
    skills: ["Prompt engineering", "Context engineering", "Evaluation", "Source grounding", "Human review"],
    icon: "message",
  },
  {
    id: "governance-safety",
    name: "AI Governance & Safety Practice",
    level: "Intermediate",
    duration: "5 weeks",
    commitment: "4–6 hours per week",
    category: "Responsible AI",
    outcome:
      "A practical governance playbook with risk categories, approval gates, disclosure rules, evidence logs and escalation paths.",
    project: "Operationalize responsible-use controls around one real AI workflow or learning context.",
    credential: "Responsible AI Practitioner",
    audiences: ["learner-self", "organization", "institution"],
    minAge: 18,
    skills: ["Risk classification", "Human review", "Policy operations", "Evidence", "Safeguarding"],
    icon: "shield",
  },
  {
    id: "product-design",
    name: "AI Product Design Studio",
    level: "Intermediate",
    duration: "6 weeks",
    commitment: "5–7 hours per week",
    category: "Product design",
    outcome:
      "A tested AI product prototype with research evidence, system boundaries and a build-ready product specification.",
    project:
      "Create and validate an AI product concept with realistic states, recovery and review boundaries.",
    credential: "AI Product Designer",
    audiences: ["learner-self", "organization", "institution"],
    minAge: 16,
    skills: ["Product strategy", "Research", "Interaction design", "Prototyping", "Evaluation"],
    icon: "star",
  },
  {
    id: "data-knowledge",
    name: "AI Data & Knowledge Systems",
    level: "Intermediate",
    duration: "6 weeks",
    commitment: "5–8 hours per week",
    category: "Knowledge systems",
    outcome:
      "A governed knowledge workflow with quality checks, retrieval structure, citations and an evidence-based review process.",
    project: "Build a source-grounded knowledge assistant or retrieval workflow using approved information.",
    credential: "AI Knowledge Systems Builder",
    audiences: ["learner-self", "organization", "institution"],
    minAge: 16,
    skills: ["Data preparation", "Knowledge design", "Retrieval", "Citations", "Quality review"],
    icon: "book",
  },
  {
    id: "automation-workflows",
    name: "AI Automation & Workflow Systems",
    level: "Intermediate",
    duration: "6 weeks",
    commitment: "5–8 hours per week",
    category: "Automation",
    outcome:
      "A working automation with test evidence, exception handling, approval, monitoring and an operating guide.",
    project:
      "Build one end-to-end AI-assisted workflow that improves work while preserving human accountability.",
    credential: "AI Workflow Builder",
    audiences: ["learner-self", "organization", "institution"],
    minAge: 16,
    skills: ["Process mapping", "Integration design", "Automation", "Approvals", "Monitoring"],
    icon: "settings",
  },
  {
    id: "app-builder",
    name: "AI-App & Website Builder Lab",
    level: "Advanced",
    duration: "8 weeks",
    commitment: "8–12 hours per week",
    category: "Product building",
    outcome:
      "A working full-stack AI product with architecture, evaluation, safety boundaries and proof-of-work evidence.",
    project: "Design, build, verify and deploy an AI-enabled web application another person can use.",
    credential: "AI Product Builder",
    audiences: ["learner-self", "organization", "institution"],
    minAge: 16,
    skills: [
      "Product strategy",
      "TypeScript",
      "API design",
      "Data",
      "AI integration",
      "Testing",
      "Deployment",
    ],
    icon: "laptop",
  },
  {
    id: "team-institution",
    name: "Team & Institution AI Programs",
    level: "Organization",
    duration: "Configured",
    commitment: "Configured by role",
    category: "Organization capability",
    outcome:
      "A configured capability program with role-based learning, practical projects, governance and decision-ready evidence.",
    project:
      "Deliver a private cohort aligned to real workflows, adoption goals, safeguards and measurable outcomes.",
    credential: "Configured credential pathway",
    audiences: ["organization", "institution"],
    minAge: 5,
    skills: ["Role tracks", "Private cohorts", "Workflow pilots", "Governance", "Adoption reporting"],
    icon: "building",
  },
];

export const COHORTS: CohortRecord[] = [
  {
    id: "everyday-5-7-oct-2026",
    programId: "everyday-ai",
    trackId: "age-5-7",
    name: "October 2026 Family Cohort",
    delivery: "Parent-guided live cohort",
    startDate: "2026-10-03",
    endDate: "2026-10-24",
    applicationDeadline: "2026-09-24",
    schedule: "Saturdays · 10:00–11:00",
    timezone: "Africa/Kampala",
    language: "English + family language support",
    status: "open",
    capacity: 24,
    enrolled: 11,
    format: "live",
  },
  {
    id: "everyday-5-7-jan-2027",
    programId: "everyday-ai",
    trackId: "age-5-7",
    name: "January 2027 Family Cohort",
    delivery: "Parent-guided live cohort",
    startDate: "2027-01-16",
    endDate: "2027-02-06",
    applicationDeadline: "2027-01-07",
    schedule: "Saturdays · 09:00–10:00",
    timezone: "Africa/Kampala",
    language: "English",
    status: "planned",
    capacity: 24,
    enrolled: 0,
    format: "live",
  },
  {
    id: "everyday-8-11-oct-2026",
    programId: "everyday-ai",
    trackId: "age-8-11",
    name: "October 2026 Young Creators",
    delivery: "Live cohort + family orientation",
    startDate: "2026-10-03",
    endDate: "2026-10-24",
    applicationDeadline: "2026-09-24",
    schedule: "Saturdays · 11:30–13:00",
    timezone: "Africa/Kampala",
    language: "English",
    status: "limited",
    capacity: 28,
    enrolled: 23,
    format: "live",
  },
  {
    id: "everyday-8-11-jan-2027",
    programId: "everyday-ai",
    trackId: "age-8-11",
    name: "January 2027 Young Creators",
    delivery: "Live cohort + family orientation",
    startDate: "2027-01-16",
    endDate: "2027-02-06",
    applicationDeadline: "2027-01-07",
    schedule: "Saturdays · 11:00–12:30",
    timezone: "Africa/Kampala",
    language: "English + French support",
    status: "planned",
    capacity: 28,
    enrolled: 0,
    format: "live",
  },
  {
    id: "everyday-12-17-oct-2026",
    programId: "everyday-ai",
    trackId: "age-12-17",
    name: "October 2026 Youth Cohort",
    delivery: "Live cohort + project",
    startDate: "2026-10-01",
    endDate: "2026-10-22",
    applicationDeadline: "2026-09-22",
    schedule: "Thursdays · 17:00–18:30",
    timezone: "Africa/Kampala",
    language: "English",
    status: "open",
    capacity: 32,
    enrolled: 18,
    format: "live",
  },
  {
    id: "everyday-12-17-jan-2027",
    programId: "everyday-ai",
    trackId: "age-12-17",
    name: "January 2027 Global Youth Cohort",
    delivery: "Live online cohort",
    startDate: "2027-01-12",
    endDate: "2027-02-02",
    applicationDeadline: "2027-01-03",
    schedule: "Tuesdays · 16:00–17:30",
    timezone: "UTC",
    language: "English",
    status: "planned",
    capacity: 36,
    enrolled: 0,
    format: "live",
  },
  {
    id: "everyday-adult-oct-2026",
    programId: "everyday-ai",
    trackId: "age-18-plus",
    name: "October 2026 Global Cohort",
    delivery: "Guided online + live Class",
    startDate: "2026-10-01",
    endDate: "2026-10-22",
    applicationDeadline: "2026-09-22",
    schedule: "Thursdays · 18:00–19:30",
    timezone: "Africa/Kampala",
    language: "English",
    status: "open",
    capacity: 48,
    enrolled: 21,
    format: "live",
  },
  {
    id: "everyday-adult-jan-2027",
    programId: "everyday-ai",
    trackId: "age-18-plus",
    name: "January 2027 Africa & EMEA Cohort",
    delivery: "Guided online + live Class",
    startDate: "2027-01-12",
    endDate: "2027-02-02",
    applicationDeadline: "2027-01-03",
    schedule: "Tuesdays · 18:00–19:30",
    timezone: "Africa/Kampala",
    language: "English + French support",
    status: "planned",
    capacity: 48,
    enrolled: 0,
    format: "live",
  },
  {
    id: "prompt-oct-2026",
    programId: "prompt-context",
    name: "October 2026 Global Cohort",
    delivery: "Guided online + live Class",
    startDate: "2026-10-01",
    endDate: "2026-10-29",
    applicationDeadline: "2026-09-20",
    schedule: "Thursdays · 18:00–19:30",
    timezone: "Africa/Kampala",
    language: "English",
    status: "open",
    capacity: 40,
    enrolled: 17,
    format: "live",
  },
  {
    id: "prompt-jan-2027",
    programId: "prompt-context",
    name: "January 2027 Global Cohort",
    delivery: "Guided online + live Class",
    startDate: "2027-01-14",
    endDate: "2027-02-11",
    applicationDeadline: "2027-01-04",
    schedule: "Thursdays · 17:00–18:30",
    timezone: "UTC",
    language: "English",
    status: "planned",
    capacity: 40,
    enrolled: 0,
    format: "live",
  },
  {
    id: "governance-oct-2026",
    programId: "governance-safety",
    name: "October 2026 Practitioner Cohort",
    delivery: "Live cohort + governance project",
    startDate: "2026-10-05",
    endDate: "2026-11-09",
    applicationDeadline: "2026-09-25",
    schedule: "Mondays · 17:30–19:30",
    timezone: "Africa/Kampala",
    language: "English",
    status: "limited",
    capacity: 30,
    enrolled: 26,
    format: "live",
  },
  {
    id: "governance-jan-2027",
    programId: "governance-safety",
    name: "January 2027 Practitioner Cohort",
    delivery: "Live cohort + governance project",
    startDate: "2027-01-18",
    endDate: "2027-02-22",
    applicationDeadline: "2027-01-08",
    schedule: "Mondays · 17:00–19:00",
    timezone: "UTC",
    language: "English",
    status: "planned",
    capacity: 30,
    enrolled: 0,
    format: "live",
  },
  {
    id: "design-oct-2026",
    programId: "product-design",
    name: "October 2026 Design Studio",
    delivery: "Live studio + prototype review",
    startDate: "2026-10-06",
    endDate: "2026-11-17",
    applicationDeadline: "2026-09-25",
    schedule: "Tuesdays · 17:00–19:00",
    timezone: "Africa/Kampala",
    language: "English",
    status: "open",
    capacity: 28,
    enrolled: 13,
    format: "live",
  },
  {
    id: "design-feb-2027",
    programId: "product-design",
    name: "February 2027 Design Studio",
    delivery: "Live studio + prototype review",
    startDate: "2027-02-02",
    endDate: "2027-03-16",
    applicationDeadline: "2027-01-22",
    schedule: "Tuesdays · 17:00–19:00",
    timezone: "UTC",
    language: "English",
    status: "planned",
    capacity: 28,
    enrolled: 0,
    format: "live",
  },
  {
    id: "data-oct-2026",
    programId: "data-knowledge",
    name: "October 2026 Knowledge Systems Cohort",
    delivery: "Live cohort + governed build",
    startDate: "2026-10-07",
    endDate: "2026-11-18",
    applicationDeadline: "2026-09-27",
    schedule: "Wednesdays · 18:00–20:00",
    timezone: "Africa/Kampala",
    language: "English",
    status: "open",
    capacity: 30,
    enrolled: 14,
    format: "live",
  },
  {
    id: "data-feb-2027",
    programId: "data-knowledge",
    name: "February 2027 Knowledge Systems Cohort",
    delivery: "Live cohort + governed build",
    startDate: "2027-02-03",
    endDate: "2027-03-17",
    applicationDeadline: "2027-01-24",
    schedule: "Wednesdays · 17:00–19:00",
    timezone: "UTC",
    language: "English",
    status: "planned",
    capacity: 30,
    enrolled: 0,
    format: "live",
  },
  {
    id: "automation-oct-2026",
    programId: "automation-workflows",
    name: "October 2026 Workflow Builders",
    delivery: "Live cohort + automation project",
    startDate: "2026-10-08",
    endDate: "2026-11-19",
    applicationDeadline: "2026-09-28",
    schedule: "Thursdays · 17:30–19:30",
    timezone: "Africa/Kampala",
    language: "English",
    status: "open",
    capacity: 30,
    enrolled: 16,
    format: "live",
  },
  {
    id: "automation-feb-2027",
    programId: "automation-workflows",
    name: "February 2027 Workflow Builders",
    delivery: "Live cohort + automation project",
    startDate: "2027-02-04",
    endDate: "2027-03-18",
    applicationDeadline: "2027-01-25",
    schedule: "Thursdays · 17:00–19:00",
    timezone: "UTC",
    language: "English",
    status: "planned",
    capacity: 30,
    enrolled: 0,
    format: "live",
  },
  {
    id: "builder-oct-2026",
    programId: "app-builder",
    name: "October 2026 Product Builder Cohort",
    delivery: "Live studio + AI Labs + Build Rooms",
    startDate: "2026-10-01",
    endDate: "2026-11-26",
    applicationDeadline: "2026-09-18",
    schedule: "Wednesdays · 17:00–19:00 + Saturday Lab",
    timezone: "Africa/Kampala",
    language: "English",
    status: "limited",
    capacity: 24,
    enrolled: 21,
    format: "live",
  },
  {
    id: "builder-jan-2027",
    programId: "app-builder",
    name: "January 2027 Product Builder Cohort",
    delivery: "Live studio + AI Labs + Build Rooms",
    startDate: "2027-01-13",
    endDate: "2027-03-10",
    applicationDeadline: "2027-01-02",
    schedule: "Wednesdays · 17:00–19:00 + Saturday Lab",
    timezone: "UTC",
    language: "English",
    status: "planned",
    capacity: 24,
    enrolled: 0,
    format: "live",
  },
  {
    id: "team-private-2026",
    programId: "team-institution",
    name: "Private Team Cohort — 2026 intake",
    delivery: "Configured private delivery",
    startDate: "2026-10-01",
    endDate: "2026-12-18",
    applicationDeadline: "2026-09-15",
    schedule: "Agreed during program design",
    timezone: "Organization time zone",
    language: "Configured by cohort",
    status: "open",
    capacity: 250,
    enrolled: 84,
    format: "private",
  },
  {
    id: "institution-private-2027",
    programId: "team-institution",
    name: "Institution Cohort — 2027 intake",
    delivery: "Configured institutional delivery",
    startDate: "2027-01-18",
    endDate: "2027-04-30",
    applicationDeadline: "2026-12-18",
    schedule: "Configured around academic calendar",
    timezone: "Institution time zone",
    language: "Configured by cohort",
    status: "planned",
    capacity: 500,
    enrolled: 0,
    format: "private",
  },
];

export const COUNTRIES = COUNTRY_OPTIONS;

export const LANGUAGES = [...LANGUAGE_NAMES, "Other"];

export const TIMEZONES = [
  "Africa/Kampala",
  "Africa/Nairobi",
  "Africa/Kigali",
  "Africa/Dar_es_Salaam",
  "Africa/Lagos",
  "Africa/Accra",
  "Africa/Johannesburg",
  "Africa/Cairo",
  "Africa/Addis_Ababa",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Madrid",
  "Europe/Amsterdam",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Sao_Paulo",
  "Asia/Dubai",
  "Asia/Riyadh",
  "Asia/Kolkata",
  "Asia/Singapore",
  "Asia/Shanghai",
  "Asia/Tokyo",
  "Australia/Sydney",
  "Pacific/Auckland",
  "UTC",
];

export const CURRENCIES = ["UGX", "USD", "GBP", "EUR", "KES", "NGN", "ZAR", "CAD", "AUD", "AED", "CNY"];
export const AVAILABILITY = [
  "Weekday mornings",
  "Weekday afternoons",
  "Weekday evenings",
  "Saturday mornings",
  "Saturday afternoons",
  "Sunday",
  "Flexible / self-paced",
];
export const ACCOMMODATIONS = [
  "Live captions",
  "Screen-reader compatible materials",
  "Keyboard-only participation",
  "Larger text / higher contrast",
  "Reduced motion",
  "Extra time",
  "Scheduled breaks",
  "Interpreter / translation",
  "Low-bandwidth option",
  "Alternative identity verification",
];

export const APPLICATION_ROUTE_CARDS: Array<{
  id: ApplicationRoute;
  title: string;
  description: string;
  icon: IconName;
  badge: string;
}> = [
  {
    id: "learner-self",
    title: "Apply for myself",
    description: "Choose a Program, age track where applicable and a specific cohort.",
    icon: "graduation",
    badge: "Individual learner",
  },
  {
    id: "parent-child",
    title: "Apply for a child",
    description: "Parent or guardian-led application with age-aware consent and access.",
    icon: "child",
    badge: "Family route",
  },
  {
    id: "teaching",
    title: "Teach, mentor or assess",
    description: "Apply for an approved instructional or human-assessment role.",
    icon: "badge",
    badge: "Professional application",
  },
  {
    id: "proctor",
    title: "Become an exam proctor",
    description: "Apply to operate assigned assessments without grading authority.",
    icon: "eye",
    badge: "Assessment operations",
  },
  {
    id: "organization",
    title: "Enroll an organization or team",
    description: "Configure private cohorts, role tracks, governance and sponsorship.",
    icon: "building",
    badge: "Organization",
  },
  {
    id: "institution",
    title: "Enroll a school or institution",
    description: "Configure academic structure, safeguarding, records and delivery.",
    icon: "school",
    badge: "Education institution",
  },
  {
    id: "invited-member",
    title: "I have an invitation",
    description: "Accept a role, cohort, guardian, team or institution invitation.",
    icon: "mail",
    badge: "Invitation",
  },
];

export const initialApplicant: ApplicantProfile = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  country: "Uganda",
  city: "",
  timezone: "Africa/Kampala",
  language: "English",
  secondaryLanguage: "Luganda",
  dateOfBirth: "",
  gender: "Prefer not to say",
  occupation: "",
  organization: "",
  referral: "Chatoner website",
};

export const initialLearner: LearnerProfile = {
  firstName: "",
  lastName: "",
  preferredName: "",
  dateOfBirth: "",
  schoolOrEmployer: "",
  gradeOrRole: "",
  learnerEmail: "",
  createLearnerLogin: false,
  assent: false,
  supportNotes: "",
};

export const initialGuardian: GuardianProfile = {
  required: false,
  invitationSent: false,
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  relationship: "Parent",
  legalAuthority: "Parent / legal guardian",
  communicationRole: "Primary family contact",
  paymentResponsibility: false,
  consentIdentity: false,
  consentProgram: false,
  consentData: false,
  consentRecording: false,
  consentSafeguarding: false,
  signature: "",
};

export const initialEntity: EntityProfile = {
  legalName: "",
  tradingName: "",
  type: "",
  registrationNumber: "",
  website: "",
  country: "Uganda",
  headquarters: "",
  teamSize: "",
  learnerCount: "",
  representativeTitle: "",
  financeEmail: "",
  procurementRequired: false,
  purchaseOrderRequired: false,
  ssoRequired: false,
  scimRequired: false,
  dataRegion: "To be agreed",
  goals: "",
};

export const initialProfessional: ProfessionalProfile = {
  currentTitle: "",
  employer: "",
  yearsExperience: "",
  qualifications: "",
  teachingOrProctoringExperience: "",
  programs: [],
  languages: [],
  weeklyAvailability: "",
  referenceOne: "",
  referenceTwo: "",
  identityDocument: "",
  backgroundCheck: "Not yet provided",
  safeguardingTraining: false,
  accessibilityTraining: false,
  conflictDeclaration: false,
  authorityDeclaration: false,
};

export const initialApplication: ApplicationDraft = {
  reference: makeReference("APP"),
  route: "",
  roleId: "learner",
  applicant: initialApplicant,
  learner: initialLearner,
  guardian: initialGuardian,
  entity: initialEntity,
  professional: initialProfessional,
  programId: "",
  trackId: "",
  cohortId: "",
  deliveryPreference: "",
  goal: "",
  projectIdea: "",
  currentAIUse: "",
  readiness: { literacy: 2, prompting: 2, verification: 1, technical: 1, governance: 1 },
  availability: [],
  weeklyHours: "",
  accessibilityNeeds: "",
  accommodations: [],
  lowBandwidth: false,
  funding: "",
  currency: "UGX",
  scholarshipReason: "",
  sponsorName: "",
  sponsorEmail: "",
  consent: {
    privacy: false,
    terms: false,
    conduct: false,
    aiUse: false,
    recording: false,
    communications: false,
    dataAccuracy: false,
  },
  signature: "",
  declaration: false,
  status: "draft",
  completedSteps: [],
  lastSavedAt: new Date().toISOString(),
};

export const initialOnboarding: OnboardingDraft = {
  roleId: "learner",
  completedSteps: [],
  profileConfirmed: false,
  mfaConfirmed: false,
  recoveryCodesSaved: false,
  languageConfirmed: false,
  accessibilityConfirmed: false,
  deviceChecked: false,
  notifications: [],
  connectedApps: [],
  linkedLearners: [],
  teachingPrograms: [],
  policyChecks: {},
  diagnostic: { confidence: 2, verification: 2, collaboration: 2, safeguarding: 2 },
  workspaceName: "",
  completedAt: "",
};

export const initialState: RootState = {
  screen: "welcome",
  authenticated: false,
  emailVerified: false,
  mfaVerified: false,
  authEmail: "",
  authName: "",
  selectedWorkspace: "",
  applicationStep: 0,
  onboardingStep: 0,
  application: initialApplication,
  onboarding: initialOnboarding,
};

export function safeLoadState(): RootState {
  if (typeof window === "undefined") return initialState;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "null") as RootState | null;
    if (!parsed || !parsed.application || !parsed.onboarding) return initialState;
    return {
      ...initialState,
      ...parsed,
      application: {
        ...initialApplication,
        ...parsed.application,
        applicant: { ...initialApplicant, ...parsed.application.applicant },
        learner: { ...initialLearner, ...parsed.application.learner },
        guardian: { ...initialGuardian, ...parsed.application.guardian },
        entity: { ...initialEntity, ...parsed.application.entity },
        professional: { ...initialProfessional, ...parsed.application.professional },
        consent: { ...initialApplication.consent, ...parsed.application.consent },
      },
      onboarding: { ...initialOnboarding, ...parsed.onboarding },
    };
  } catch {
    return initialState;
  }
}

export const STEP_DEFINITIONS: Record<ApplicationStepId, ApplicationStepDef> = {
  route: {
    id: "route",
    label: "Choose your route",
    short: "Route",
    description: "Tell us who is entering the Academy and why.",
    icon: "arrow-right",
    minutes: 2,
  },
  profile: {
    id: "profile",
    label: "Applicant profile",
    short: "Profile",
    description: "Identity, contact, language and location details.",
    icon: "user",
    minutes: 5,
  },
  learner: {
    id: "learner",
    label: "Learner & age",
    short: "Learner",
    description: "Age-aware learner details and account model.",
    icon: "child",
    minutes: 5,
  },
  entity: {
    id: "entity",
    label: "Organization or institution",
    short: "Entity",
    description: "Legal, operational, provisioning and delivery context.",
    icon: "building",
    minutes: 7,
  },
  professional: {
    id: "professional",
    label: "Professional role",
    short: "Role",
    description: "Qualifications, experience, references and authority.",
    icon: "badge",
    minutes: 8,
  },
  program: {
    id: "program",
    label: "Program & pathway",
    short: "Program",
    description: "Choose the capability outcome and age track where applicable.",
    icon: "book",
    minutes: 5,
  },
  cohort: {
    id: "cohort",
    label: "Cohort selection",
    short: "Cohort",
    description: "Choose the exact intake, schedule, time zone and language.",
    icon: "calendar",
    minutes: 4,
  },
  readiness: {
    id: "readiness",
    label: "Goals & readiness",
    short: "Readiness",
    description: "Help Admissions confirm fit and preparation.",
    icon: "chart",
    minutes: 7,
  },
  availability: {
    id: "availability",
    label: "Schedule & access",
    short: "Schedule",
    description: "Availability, accessibility, language and connectivity.",
    icon: "clock",
    minutes: 5,
  },
  funding: {
    id: "funding",
    label: "Funding & billing",
    short: "Funding",
    description: "Self-funding, sponsorship, scholarship or invoice route.",
    icon: "wallet",
    minutes: 5,
  },
  guardian: {
    id: "guardian",
    label: "Parent / guardian",
    short: "Guardian",
    description: "Verified relationship, consent and family communication.",
    icon: "heart",
    minutes: 7,
  },
  checks: {
    id: "checks",
    label: "Role checks",
    short: "Checks",
    description: "Safeguarding, identity, authority and conflict declarations.",
    icon: "shield",
    minutes: 6,
  },
  consent: {
    id: "consent",
    label: "Consent & declarations",
    short: "Consent",
    description: "Privacy, conduct, AI use, recording and accuracy.",
    icon: "clipboard",
    minutes: 5,
  },
  review: {
    id: "review",
    label: "Review & submit",
    short: "Review",
    description: "Resolve missing items and submit for human review.",
    icon: "check",
    minutes: 4,
  },
};

export const ONBOARDING_DEFS: Record<OnboardingStepId, OnboardingStepDef> = {
  welcome: {
    id: "welcome",
    label: "Welcome",
    description: "Your verified role, Program and workspace.",
    icon: "sparkles",
  },
  security: {
    id: "security",
    label: "Security",
    description: "MFA, recovery and trusted access.",
    icon: "lock",
  },
  profile: {
    id: "profile",
    label: "Preferences",
    description: "Profile, language, accessibility and notifications.",
    icon: "user",
  },
  relationships: {
    id: "relationships",
    label: "People & scope",
    description: "Linked learners, teams, cohorts or academic units.",
    icon: "users",
  },
  "role-setup": {
    id: "role-setup",
    label: "Role setup",
    description: "Assignments, structure and role-specific preparation.",
    icon: "badge",
  },
  tools: {
    id: "tools",
    label: "Connected tools",
    description: "Academy, Class, Labs and approved integrations.",
    icon: "link",
  },
  policies: {
    id: "policies",
    label: "Boundaries",
    description: "Human authority, privacy, safeguarding and evidence.",
    icon: "shield",
  },
  readiness: {
    id: "readiness",
    label: "Readiness check",
    description: "Confirm devices, skills, support and next actions.",
    icon: "clipboard",
  },
  ready: {
    id: "ready",
    label: "Ready",
    description: "Activate the correct workspace and continue.",
    icon: "check",
  },
};
